import React, { useState } from 'react';
import { PortvilleBill } from '../../../../types/portvilleBilling';

interface BillDetailModalProps {
  bill: PortvilleBill;
  onClose: () => void;
  onUpdate: (billId: string, updates: Partial<PortvilleBill>) => void;
}

const BillDetailModal: React.FC<BillDetailModalProps> = ({
  bill,
  onClose,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBill, setEditedBill] = useState(bill);

  // 상태 텍스트 반환
  const getStatusText = (status: PortvilleBill['status']) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'paid':
        return '납부완료';
      case 'overdue':
        return '연체';
      case 'cancelled':
        return '취소';
      default:
        return '알 수 없음';
    }
  };

  // 상태 색상 반환
  const getStatusColor = (status: PortvilleBill['status']) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      case 'cancelled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // 수정 저장
  const handleSave = () => {
    onUpdate(bill.id, {
      ...editedBill,
      updatedAt: new Date()
    });
    setIsEditing(false);
  };

  // 수정 취소
  const handleCancel = () => {
    setEditedBill(bill);
    setIsEditing(false);
  };

  // 관리비 항목 표시 컴포넌트
  const BillItemRow: React.FC<{
    label: string;
    amount: number | undefined;
    field?: keyof PortvilleBill['billItems'];
  }> = ({ label, amount, field }) => (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-700">{label}</span>
      <div className="text-right">
        {isEditing && field ? (
          <input
            type="number"
            value={editedBill.billItems[field] || 0}
            onChange={(e) => setEditedBill(prev => ({
              ...prev,
              billItems: {
                ...prev.billItems,
                [field]: parseInt(e.target.value) || 0
              }
            }))}
            className="w-24 text-right border border-gray-300 rounded px-2 py-1 text-sm"
          />
        ) : (
          <span className="font-medium">{(amount || 0).toLocaleString()}원</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 모달 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">청구서 상세</h2>
            <p className="text-gray-600 mt-1">
              {bill.roomNumber} · {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                수정
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  저장
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-600 hover:text-gray-700 text-sm"
                >
                  취소
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">입주자 정보</h3>
              <div className="space-y-2">
                {bill.roomType === '시설물' && bill.facilityInfo ? (
                  <>
                    <div>
                      <span className="text-sm text-gray-600">시설물명:</span>
                      <span className="ml-2 font-medium">{bill.facilityInfo.facilityName}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">시설물 타입:</span>
                      <span className="ml-2 font-medium">{bill.facilityInfo.facilityType}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">담당자:</span>
                      <span className="ml-2 font-medium">{bill.facilityInfo.responsiblePerson.name}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">연락처:</span>
                      <span className="ml-2 font-medium">{bill.facilityInfo.responsiblePerson.phoneNumber}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">소속:</span>
                      <span className="ml-2 font-medium">{bill.facilityInfo.responsiblePerson.organization}</span>
                    </div>
                  </>
                ) : bill.residentInfo ? (
                  <>
                    <div>
                      <span className="text-sm text-gray-600">이름:</span>
                      <span className="ml-2 font-medium">{bill.residentInfo.name}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">전화번호:</span>
                      <span className="ml-2 font-medium">{bill.residentInfo.phoneNumber}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">소속:</span>
                      <span className="ml-2 font-medium">{bill.residentInfo.organization}</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">청구 정보</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">상태:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                    {getStatusText(bill.status)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">발행일:</span>
                  <span className="ml-2 font-medium">
                    {new Date(bill.issuedDate).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">납부 기한:</span>
                  <span className="ml-2 font-medium">
                    {new Date(bill.dueDate).toLocaleDateString()}
                  </span>
                </div>
                {bill.paidDate && (
                  <div>
                    <span className="text-sm text-gray-600">납부일:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {new Date(bill.paidDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 청구 기간 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">청구 기간</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-center">
                <span className="text-2xl font-bold text-gray-900">
                  {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
                </span>
                <div className="text-sm text-gray-600 mt-1">
                  {new Date(bill.billingPeriod.startDate).toLocaleDateString()} ~ 
                  {new Date(bill.billingPeriod.endDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* 사용량 정보 */}
          {bill.usageInfo && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">사용량 정보</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">전기 사용량</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">개별 사용량:</span>
                        <span className="font-medium">
                          {bill.usageInfo.electricityIndividualUsage !== undefined && bill.usageInfo.electricityIndividualUsage !== null
                            ? `${bill.usageInfo.electricityIndividualUsage.toLocaleString()} kWh`
                            : '미입력'
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">공용 사용량:</span>
                        <span className="font-medium">
                          {bill.usageInfo.electricityCommonUsage !== undefined && bill.usageInfo.electricityCommonUsage !== null
                            ? `${bill.usageInfo.electricityCommonUsage.toLocaleString()} kWh`
                            : '미입력'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">수도 사용량</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">개별 사용량:</span>
                        <span className="font-medium">
                          {bill.usageInfo.waterIndividualUsage !== undefined && bill.usageInfo.waterIndividualUsage !== null
                            ? `${bill.usageInfo.waterIndividualUsage.toLocaleString()} 톤`
                            : '미입력'
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">공용 사용량:</span>
                        <span className="font-medium">
                          {bill.usageInfo.waterCommonUsage !== undefined && bill.usageInfo.waterCommonUsage !== null
                            ? `${bill.usageInfo.waterCommonUsage.toLocaleString()} 톤`
                            : '미입력'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 관리비 상세 내역 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">관리비 상세 내역</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-1">
                <BillItemRow 
                  label="월 임대료" 
                  amount={bill.billItems.monthlyRent} 
                  field="monthlyRent"
                />
                <BillItemRow 
                  label="관리비" 
                  amount={bill.billItems.managementFee} 
                  field="managementFee"
                />
                <BillItemRow 
                  label="부가세 (관리비)" 
                  amount={bill.billItems.managementFeeVAT} 
                  field="managementFeeVAT"
                />
                <BillItemRow 
                  label="전기요금" 
                  amount={bill.billItems.electricityFee} 
                  field="electricityFee"
                />
                <BillItemRow 
                  label="전기요금 (부가세)" 
                  amount={bill.billItems.electricityFeeVAT} 
                  field="electricityFeeVAT"
                />
                <BillItemRow 
                  label="전력기금" 
                  amount={bill.billItems.electricityFund} 
                  field="electricityFund"
                />
                <BillItemRow 
                  label="수도요금" 
                  amount={bill.billItems.waterFee} 
                  field="waterFee"
                />
                <BillItemRow 
                  label="통신요금" 
                  amount={bill.billItems.communicationFee} 
                  field="communicationFee"
                />
                <BillItemRow 
                  label="통신요금 (부가세)" 
                  amount={bill.billItems.communicationFeeVAT} 
                  field="communicationFeeVAT"
                />
                <BillItemRow 
                  label="상계처리비" 
                  amount={bill.billItems.offsetProcessingFee} 
                  field="offsetProcessingFee"
                />
                <BillItemRow 
                  label="상계처리비 (부가세)" 
                  amount={bill.billItems.offsetProcessingFeeVAT} 
                  field="offsetProcessingFeeVAT"
                />
                <BillItemRow 
                  label="미납금" 
                  amount={bill.billItems.overdueAmount || 0} 
                  field="overdueAmount"
                />
                <BillItemRow 
                  label="연체 가산금" 
                  amount={bill.billItems.overduePenalty || 0} 
                  field="overduePenalty"
                />

                {/* 총액 */}
                <div className="border-t-2 border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">총 청구 금액</span>
                    <span className="text-xl font-bold text-blue-600">
                      {isEditing ? (
                        Object.values(editedBill.billItems).reduce((sum, amount) => sum + amount, 0).toLocaleString()
                      ) : (
                        bill.totalAmount.toLocaleString()
                      )}원
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 메모 영역 (필요시) */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">관리자 메모</h3>
            <textarea
              value=""
              placeholder="청구서 관련 메모를 입력하세요..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillDetailModal;
